import * as STD from '@emersya/mpa-std-lib';

import { Text, Block, Field, InputBool } from '@emersya/mpa-components-lib';
import CM                                from '@emersya/mpa-components-manager-lib';
import ViewerComponent                   from '../../../viewer/Viewer/Viewer';
import { NS as StandingArchNS }          from '../StandingArch/StandingArch';
import { NS as StandingBannerConfortNS } from '../StandingBannerConfort/StandingBannerConfort';
import { NS as StandingLighting }        from '../StandingLighting/StandingLighting';
import { NS as StandingPlinth }          from '../StandingPlinth/StandingPlinth';
import { NS as StandingObjectsPositioningDynamicsSets }          from '../StandingObjectsPositioningDynamicsSets/StandingObjectsPositioningDynamicsSets';
import { NS as PlannerWallsEditorNS }    from '../../../viewer/planner/walls/Editor/PlannerWallsEditor';
import computeMatrix                     from './computeMatrix';
import computeMatrixBanner               from './computeMatrixBanner';
import computeMatrixLightning            from './computeMatrixLightning';

export interface IStandingStorageComponentConfiguration extends CM.DOMComponent.IConfiguration {
	translations : Record<string, STD.translationsManager.TranslationMapping>;
};

export class StandingStorageComponent extends CM.DOMComponent<
	HTMLDivElement,
	StandingStorageComponent,
	IStandingStorageComponentConfiguration
>
{
	static override initialize() {
		super.initialize();
	};

	component_DOM : HTMLDivElement;
	computeMatrix = computeMatrix;
	computeMatrixBanner = computeMatrixBanner;
	computeMatrixLightning = computeMatrixLightning;
	readonly plannerCategory! : ViewerComponent.ProductCategory;
	viewerNode : ViewerComponent.ProductInstance | null = null;
	isStorage : boolean = true;
	isStorageBanner : boolean = true;
	positionStorage = {
		start : 0,
		end :   0
	};

	get globalLayoutId() : string | null {
		const product = app.currentProduct;
		if (!product) {
			this.component_throwUserError('No current product available!');
		}
		const layoutPart = product.getPart([ 'layout' ]);
		if (!layoutPart) {
			return null;
		}
		const layoutId = app.isReloading ?
			app.previousConfigurationMetadata?.basket.products[ 0 ]?.parts.find(x => x.name === 'layout')?.currentOption?.name :
			layoutPart.currentOption?.id
		;
		return layoutId || null;
	};

	getWDH() {
		const p = app.currentProduct;
		if (!p) {
			this.component_throwUserError('');
		}
		const dpart = p.getPart([ 'dimensions' ]);
		const r = {
			width :  Number.parseFloat(dpart.getPart([ 'width' ])?.reference?.value || <string> <any> 1),
			depth :  Number.parseFloat(dpart.getPart([ 'depth' ])?.reference?.value || <string> <any> 1),
			height : Number.parseFloat(dpart.getPart([ 'height' ])?.reference?.value || <string> <any> 1)
		};
		if ((r.width <= 1 || r.height <= 1 || r.depth <= 1) && app.isReloading) {
			return app.previousConfigurationMetadata?.basket.products[ 0 ]?.parts.find(x => x.name === 'dimensions')?.
			parts.
			reduce<{ width : number; depth : number; height : number; }>((a, c) => {
				(<any> a)[ c.name ] = Number.parseFloat(c.reference?.value || '1');
				return a;
			}, <any> {}) || r;
		}
		return r;
	};

	getWallThickness() {
		// TODO : DEHARDCODE
		return 0.05;
	};

	hasStorage() {
		return !!this.viewerNode;
	};

	usedSpace() {
		const wdh = this.getWDH();
		const positionStorage = this.positionStorage;
		const result = {
			back :    0,
			lateral : 0
		};
		const product = app.currentProduct;
		if (!product) {
			throw STD.errors.user('No current product available!');
		}
		const layoutPart = product.getPart([ 'layout' ]);
		if (!layoutPart) {
			throw STD.errors.user('No current product available!');
		}
		const layoutCurrentOption = layoutPart.currentOption;
		if (!layoutCurrentOption) {
			return;
		}
		if (positionStorage.start != 0 || positionStorage.end != 0) {
			if (layoutCurrentOption.id === 'shape-back') {
				result.back = positionStorage.end - positionStorage.start;
			} else if (layoutCurrentOption.id === 'shape-back-left') {
				if (positionStorage.end < wdh.depth) {
					result.lateral = positionStorage.end - positionStorage.start;
				} else if (positionStorage.start < wdh.depth) {
					result.lateral = wdh.depth - positionStorage.start;
					result.back = positionStorage.end - wdh.depth;
				} else {
					result.back = positionStorage.end - positionStorage.start;
				}
			} else if (layoutCurrentOption.id === 'shape-back-right') {
				if (positionStorage.end < wdh.width) {
					result.back = positionStorage.end - positionStorage.start;
				} else if (positionStorage.start < wdh.width) {
					result.back = wdh.width - positionStorage.start;
					result.lateral = positionStorage.end - wdh.width;
				} else {
					result.lateral = positionStorage.end - positionStorage.start;
				}
			} else if (layoutCurrentOption.id === 'shape-left-back-right') {
				if (positionStorage.end < wdh.depth) {
					result.lateral = positionStorage.end - positionStorage.start;
				} else if (positionStorage.start < wdh.depth) {
					result.lateral = wdh.depth - positionStorage.start;
					result.back = positionStorage.end - wdh.depth;
				} else if (positionStorage.end < (wdh.width + wdh.depth)) {
					result.back = positionStorage.end - positionStorage.start;
				} else if (positionStorage.start < (wdh.depth + wdh.width)) {
					result.back = wdh.depth + wdh.width - positionStorage.start;
					result.lateral = positionStorage.end - wdh.depth - wdh.width;
				} else {
					result.lateral = positionStorage.end - positionStorage.start;
				}
			}
		}
		return result;
	};

	async updateDisclaimerBanner(product : string) {
		const infoBannerSIAE = <Text.Component> this.component_find('@:disclaimerContainer.infoBannerSIAE');
		const infoBannerConfort = <Text.Component> this.component_find('@:disclaimerContainer.infoBannerConfort');
		const storageBanner = <Field.Component> this.component_find('@:storageBanner');
		const storageConfortBanner = <Field.Component> this.component_find('@:storageBannerConfort');
		const wdh = this.getWDH();
		const surface = wdh.depth * wdh.width;
		if (product === 'confort') {
			await infoBannerSIAE.component_disable();
			await infoBannerConfort.component_enable();
			await storageBanner.component_disable();
			await storageConfortBanner.component_enable();
		} else if (product === 'siae') {
			if (surface > 24) {
				await infoBannerSIAE.component_enable();
				await storageBanner.component_enable();
			} else {
				await storageBanner.component_disable();
				await infoBannerSIAE.component_disable();
			}
			await infoBannerConfort.component_disable();
			await storageConfortBanner.component_disable();
		} else {
			await infoBannerSIAE.component_disable();
			await infoBannerConfort.component_disable();
			await storageBanner.component_disable();
			await storageConfortBanner.component_disable();
		}
	};

	async updateDisclaimer() {
		const wdh = this.getWDH();
		const disclaimer1 = <Text.Component> this.component_find('@:disclaimerContainer.disclaimer1');
		const disclaimer2 = <Text.Component> this.component_find('@:disclaimerContainer.disclaimer2');
		const disclaimer3 = <Text.Component> this.component_find('@:disclaimerContainer.disclaimer3');
		if (wdh.depth * wdh.width > 30) {
			await disclaimer2.component_disable();
			await disclaimer3.component_enable();
			await disclaimer1.component_disable();
		} else if (wdh.depth * wdh.width > 24) {
			await disclaimer1.component_disable();
			await disclaimer2.component_enable();
			await disclaimer3.component_disable();
		} else if (wdh.depth * wdh.width > 11) {
			await disclaimer1.component_enable();
			await disclaimer2.component_disable();
			await disclaimer3.component_disable();
		} else {
			await disclaimer1.component_disable();
			await disclaimer2.component_disable();
			await disclaimer3.component_disable();
		}
	};

	async insert() {
		const product = app.currentProduct;
		if (this.viewerNode || !product || !app.viewer.planner) {
			this.component_throwUserError('');
		}
		const category = app.viewer.categories.find(x => x.name === 'storage');
		if (!category) {
			this.component_throwLibraryError('');
		}
		if (app.isReloading) {
			const instances = Array.from(app.viewer.safe.value.productInstances);
			const node = instances.find(x => x.product.category === category);
			if (!node) {
				return;
			}
			this.viewerNode = node;
			const size = this.deduceAdequateStorage();
			if (!size) {
				this.component_throwLibraryError('');
			}
			this.computeMatrix(size);
			return;
		}
		const size = this.deduceAdequateStorage();
		if (!size) {
			return;
		}
		const storageProductSchema = this.plannerCategory.products.find(x => x.SKU === `@storage-${ size }`);
		if (!storageProductSchema) {
			this.component_throwLibraryError('');
		}
		await this.updateDisclaimerBanner(product.id);
		const matrix = this.computeMatrix(size);
		const matrixLights = this.computeMatrixLightning();
		// const matrixLights = app.viewer.mtx([ 0, 0, 0 ], [ 0, 0, 0 ], 90);
		if (!matrix || !matrixLights) {
			this.remove();
			return;
			// this.component_throwLibraryError('');
		}
		this.viewerNode = await storageProductSchema.instantiate({
			rootLocalId : app.viewer.globalLocalId,
			appendType : 'insert',
			basketPart : [],
			basketUseDynamicTitleBinding : true,
			metadata : {
				id :           this.id,
				customImages : [
					{
						enabled :   false,
						leftRight : 0,
						upDown :    0,
						resize :    1,
						rotate :    0,
						image :     null
					}
				]
			},
			matrixOverride : {
				'global' : matrix,
				'lightingStorage' : matrixLights
			}
		});
		this.viewerNode.setName('Storage');
		const inputBool = <InputBool.Component> this.component_find('@:storage:content.options');
		if (!inputBool) {
			throw STD.errors.user('Missing input bool in content');
		}
		if (!inputBool.value) {
			await inputBool.updateValue(true);
		}
		const inputBoolBanner = <InputBool.Component> this.component_find('@:storageBanner:content.options');
		if (!inputBoolBanner) {
			throw STD.errors.user('Missing input bool in content');
		}
		await inputBoolBanner.updateValue(true);
	};

	async checkUpdate() {
		if (this.viewerNode || this.isStorage) {
			await this.update();
			await this.updateBanner();
		}
	}

	async update() {
		await app.viewer.freezeRendering({ keepCount : true });
		if (app.isReloading) {
			if (!this.viewerNode) {
				await this.insert();
			}
		} else {
			if (this.viewerNode) {
				await this.remove();
			}
			await this.insert();
		}
		await app.viewer.unfreezeRendering({ keepCount : true });
		await this.updateDisclaimer();
		// await this.updateDisclaimerBanner(product.name);
	};

	async updateForPartitionsHeads() {
		await app.viewer.freezeRendering({ keepCount : true });
		const size = this.deduceAdequateStorage();
		if (!size) {
			this.component_throwLibraryError('');
		}
		if (!this.viewerNode) {
			this.component_throwUserError('Missing storage viewer node');
		}
		const matrix = this.computeMatrix(size);
		await app.viewer.batch([
			[
				'setProductNodesInstancesMatrices', <ViewerComponent.INative_setProductNodesInstancesMatrices_descriptor> [
				{
					localId : this.viewerNode.localId,
					matrix :  matrix
				}
			]
			]
		]);
		await app.viewer.unfreezeRendering({ keepCount : true });
	};

	updateBannerNodeShowCustomImageEditor() {
		if (this.viewerNode && this.viewerNode.metadata.customImages && this.viewerNode.metadata.customImages[ 0 ]) {
			this.viewerNode.metadata.customImages[ 0 ].showEditor = this.isStorageBanner;
		}
	};

	async updateBanner() {
		// update nodes matrices
		if (!this.viewerNode) {
			return;
		}
		const product = app.currentProduct;
		if (!product) {
			this.component_throwUserError('No current product available!');
		}
		if (product.id === 'prestige' || product.id === 'business' || product.id === 'confort') {
			this.isStorageBanner = false;
			this.updateBannerNodeShowCustomImageEditor();
			return;
		}
		const bannerComponent = this.component_find('@:storageBanner');
		const wdh = this.getWDH();
		if (product.id === 'siae') {
			if ((wdh.width * wdh.depth) < 25) {
				this.isStorageBanner = false;
				this.updateBannerNodeShowCustomImageEditor();
				await bannerComponent.component_disable();
				return;
			} else if ((wdh.width * wdh.depth) >= 25 && bannerComponent.component_isDisabled) {
				await bannerComponent.component_enable();
			}
		}
		const layoutPart = product.getPart([ 'layout' ]);
		if (!layoutPart) {
			this.component_throwUserError('No layout available!');
		}
		const layoutCurrentOption = layoutPart.currentOption;
		if (!layoutCurrentOption) {
			return;
		}
		const idMapping = this.viewerNode.idMapping;
		const storageBannerNode = idMapping[ 'storageBanner-' + product.id ];
		if (!storageBannerNode) {
			this.component_throwUserError('Missing storage banner node for product : ' + product.id);
		}
		await this.updateRemoveBanner();
		if (this.isStorageBanner) {
			await app.viewer.batch([
				[
					'setProductNodesInstancesMatrices',
					[
						{
							localId : storageBannerNode,
							matrix :  this.computeMatrixBanner()
						}
					]
				],
				this.generateBannerMaterialBatchUpdate()
			]);
		}
	};

	async updateRemoveBanner() {
		// update nodes matrices
		if (!this.viewerNode) {
			return;
		}
		const product = app.currentProduct;
		if (!product) {
			throw STD.errors.user('No current product available!');
		}
		if (product.id === 'prestige' || product.id === 'business' || product.id === 'confort') {
			this.isStorageBanner = false;
			this.updateBannerNodeShowCustomImageEditor();
			return;
		}
		const layoutPart = product.getPart([ 'layout' ]);
		if (!layoutPart) {
			throw STD.errors.user('No current product available!');
		}
		const layoutCurrentOption = layoutPart.currentOption;
		if (!layoutCurrentOption) {
			return;
		}
		const idMapping = this.viewerNode.idMapping;
		if (!idMapping) {
			this.component_throwUserError('Missing idMapping on storage');
		}
		const batch = [];
		for (const [ key, value ] of Object.entries(idMapping)) {
			if (key.startsWith('storageBanner-')) {
				batch.push({
					localId : value,
					matrix :  app.viewer.mtx([ 1, 1, 1 ], [ 0, 0, 0 ], 0)
				});
			}
		}

		this.updateBannerNodeShowCustomImageEditor();
		await app.viewer.batch([
			[
				'setProductNodesInstancesMatrices', batch
			]
		]);
	};

	async remove() {
		if (!this.viewerNode) {
			return;
		}
		await this.viewerNode.delete();
		this.viewerNode = null;
		this.isStorageBanner = false;
		this.positionStorage = { start : 0, end : 0 };
		this.updateBannerNodeShowCustomImageEditor();
	};

	async onProductChange_before(){
		await this.remove();
		this.isStorage = true;
	};

	deduceAdequateStorage() {
		const wdh = this.getWDH();
		const x = wdh.width;
		const y = wdh.height;
		const z = wdh.depth;
		void y;
		const surface = x * z;
		const product = app.currentProduct;
		const layoutId = this.globalLayoutId;
		if (!product || !layoutId) {
			this.component_throwLibraryError('');
		}
		if (product.id === 'business') {
			if (surface > 35) {
				if ([
					'shape-back-left',
					'shape-back',
					'shape-back-right',
					'shape-left-back-right'
				].includes(layoutId))
				{
					if(x< 4){
						if(layoutId === 'shape-left-back-right'){
							return null;
						}else if(layoutId === 'shape-back-left' || layoutId === 'shape-back-right'){
							return '2x1Classic';
						}
					}else if(z<4){
						if(layoutId === 'shape-left-back-right' || layoutId === 'shape-back'){
							return '2x1Classic';
						}else if(layoutId === 'shape-back-left' || layoutId === 'shape-back-right'){
							return null;
						}
					}
					return '3x1Classic';
				}
			} else if (surface > 25) {
				if ([
					'shape-back-left',
					'shape-back',
					'shape-back-right',
					'shape-left-back-right'
				].includes(layoutId))
				{
					if(x< 4){
						if(layoutId === 'shape-left-back-right'){
							return null;
						}
					}else if(z<4){
						if(layoutId === 'shape-back-left' || layoutId === 'shape-back-right'){
							return null;
						}
					}
					return '2x1Classic';
				}
			} else if (surface > 11) {
				if ([
					'shape-back-left',
					'shape-back',
					'shape-back-right',
					'shape-left-back-right'
				].includes(layoutId))
				{
					if(x< 4){
						if(layoutId === 'shape-left-back-right'){
							return null;
						}
					}else if(z<4){
						if(layoutId === 'shape-back-left' || layoutId === 'shape-back-right'){
							return null;
						}
					}
					return '1x1Classic';
				}
			}
		} else if (product.id === 'confort') {
			if (surface > 24) {
				if(x < 4){
					if (layoutId === 'shape-back') {
						return '2x1Side';
					}else if(layoutId === 'shape-back-right'){
						return '3x1CornerLeft';
					} else if ([
						'shape-back-left',
						'shape-back-right',
						'shape-left-back-right'
					].includes(layoutId))
					{
						return '2x1CornerLeft';
					}	
				}else if(z < 4 && layoutId === 'shape-back-right'){
					return '2x1CornerLeft';
				}
				if (layoutId === 'shape-back') {
					return '3x1Side';
				} else if ([
					'shape-back-left',
					'shape-back-right',
					'shape-left-back-right'
				].includes(layoutId))
				{
					return '3x1CornerLeft';
				}
			} else if (surface > 17) {
				if ([
					'shape-back-left',
					'shape-back-right',
					'shape-left-back-right'
				].includes(layoutId))
				{
					return '2x1CornerLeft';
				} else if (layoutId === 'shape-back') {
					return '2x1Side';
				}
			}
		} else if (product.id === 'prestige') {
			if (surface > 24) {
				if ([ 'shape-back', 'shape-left-back-right' ].includes(layoutId)) {
					if(z<4){
						return '2x1Classic';
					}else if(x < 4 && layoutId === 'shape-left-back-right'){
						return null;
					}
					return '3x1Classic';
				} else if (layoutId === 'shape-back-left') {
					if(z<4){
						return '2x1CornerRight';
					}
					return '3x1CornerRight';
				} else if (layoutId === 'shape-back-right') {
					if(z<4){
						return '2x1CornerLeft';
					}
					return '3x1CornerLeft';
				}
			} else if (surface > 17) {
				if ([ 'shape-back', 'shape-left-back-right' ].includes(layoutId)) {
					if(x < 4 && layoutId === 'shape-left-back-right'){
						return null;
					}
					return '2x1Classic';
				} else if (layoutId === 'shape-back-left') {
					return '2x1CornerRight';
				} else if (layoutId === 'shape-back-right') {
					return '2x1CornerLeft';
				}
			}
		} else if (product.id === 'siae') {
			if (surface > 30) {
				if ([ 'shape-back-right', 'shape-left-back-right' ].includes(layoutId)) {
					return '3x1CornerLeft';
				} else if (layoutId === 'shape-back') {
					return '3x1Classic';
				} else if (layoutId === 'shape-back-left') {
					return '3x1CornerRight';
				}
			} else if (surface > 24) {
				if ([ 'shape-back-right', 'shape-left-back-right' ].includes(layoutId)) {
					return '2x1CornerLeft';
				} else if (layoutId === 'shape-back') {
					return '2x1Classic';
				} else if (layoutId === 'shape-back-left') {
					return '2x1CornerRight';
				}
			} else if (surface > 11) {
				if ([ 'shape-back-left', 'shape-left-back-right' ].includes(layoutId)) {
					return '1x1CornerRight';
				} else if (layoutId === 'shape-back') {
					return '1x1Classic';
				} else if (layoutId === 'shape-back-right') {
					return '1x1CornerLeft';
				}
			}
		}
		return null;
	};

	async onValueUpdated(ev : STD.events.Event<any, CM.Component<any, any>>) {
		// await super.onValueUpdated(ev);
		if (ev.details.isDefaultValue) {
			return;
		}
		if (!app.basket || !app.basket.basket.currentProduct) {
			this.component_throwLibraryError('');
		}

		await app.transaction(async () => {
			if (ev.details.newValue) {
				//Update
				this.isStorage = true;
				await (<StandingArchNS.Component> app.component_find('@modules:panel.standing-arch.content')).update();
				await this.update();
				const standINGObjectPositiningDynamicSets = <StandingObjectsPositioningDynamicsSets.Component> app.component_findMaybe
				('@standing-objectsPositioningDynamicsSets');
				if(standINGObjectPositiningDynamicSets){
					await standINGObjectPositiningDynamicSets.refreshTvPositions();
				}
				await (<StandingPlinth.Component> app.component_find('K.standing-plinth')).update();
				await (<StandingBannerConfortNS.Component> this.component_find('@:storageBannerConfort')).updateWithoutRemoving();
				await (<StandingLighting.Component> app.component_find('K.standing-lighting')).update();
			} else {
				//Update Remove
				const inputBoolArchCustomImg = <InputBool.Component> this.component_find('K.modules:panel.standing-arch.content:graphic:header.enable');
				inputBoolArchCustomImg.updateValue(false);
				await (<StandingArchNS.Component> app.component_find('@modules:panel.standing-arch.content')).remove();
				this.isStorage = false;
				await this.remove();
				const standINGObjectPositiningDynamicSets = <StandingObjectsPositioningDynamicsSets.Component> app.component_findMaybe
				('@standing-objectsPositioningDynamicsSets');
				if(standINGObjectPositiningDynamicSets){
					await standINGObjectPositiningDynamicSets.refreshTvPositions();
				}
				await (<StandingPlinth.Component> app.component_find('K.standing-plinth')).update();
				const inputBoolArch = <InputBool.Component> this.component_find('K.modules:panel.standing-arch.content:field:content.options');
				inputBoolArch.updateValue(false);
				// await this.updateRemove();
				const inputBoolBanner = <InputBool.Component> this.component_find('@:storageBanner:content.options');
				if (inputBoolBanner.value) {
					await inputBoolBanner.updateValue(false);
				}
				await (<StandingLighting.Component> app.component_find('K.standing-lighting')).update();
				await (<StandingBannerConfortNS.Component> this.component_find('@:storageBannerConfort')).updateWithoutRemoving();
			}
		});
		if (app.hasThisModuleUsed('viewer-planner-walls-editor')) {
			await (<PlannerWallsEditorNS.Component> app.component_find('@modules:panel.viewer-planner-walls-editor.content')).updateSize();
		}
		this.updateBannerNodeShowCustomImageEditor();
		await app.basketUpdate(this.component_getEventWhichRepresentNoEvent());
	};

	async onValueUpdatedBanner(ev : STD.events.Event<any, CM.Component<any, any>>) {
		// await super.onValueUpdated(ev);
		if (ev.details.isDefaultValue) {
			return;
		}
		if (!app.basket || !app.basket.basket.currentProduct) {
			this.component_throwLibraryError('');
		}
		if (ev.details.newValue) {
			//Update
			if (this.viewerNode) {
				this.isStorageBanner = true;
				if (this.viewerNode?.metadata.customImages?.[ 0 ]) {
					this.viewerNode.metadata.customImages[ 0 ].enabled = true;
				}
				await this.updateBanner();
			} else {
				const inputBoolBanner = <InputBool.Component> this.component_find('@:storageBanner:content.options');
				await inputBoolBanner.updateValue(false);
			}
		} else {
			//Update Remove
			this.isStorageBanner = false;
			if (this.viewerNode?.metadata.customImages?.[ 0 ]) {
				this.viewerNode.metadata.customImages[ 0 ].enabled = false;
			}
			await this.updateRemoveBanner();
		}
		this.updateBannerNodeShowCustomImageEditor();
	};

	private generateBannerMaterialBatchUpdate() : ViewerComponent.IBatchDescriptor[0] {
		if (!this.viewerNode) {
			this.component_throwUserError('');
		}
		const product = app.currentProduct;
		if (!product) {
			this.component_throwUserError('No current product available!');
		}
		const idMapping = this.viewerNode.idMapping;
		const storageBannerNode = idMapping[ 'storageBanner-' + product.id ];
		if (!storageBannerNode) {
			this.component_throwUserError('Missing storage banner node for product : ' + product.id);
		}
		const CI = this.viewerNode.product.extension?.customImages?.[ 0 ];
		if (!CI) {
			this.component_throwLibraryError('');
		}
		return [
			'setMaterials',
			{
				nodeLocalIds : [ storageBannerNode ],
				materials :    [
					{
						configurableMaterial : CI.material.name,
						materialVariation :    this.isStorageBanner ?
																		 CI.material.configuration.enabled :
																		 CI.material.configuration.disabled
					}
				]
			}
		];
	};

	override async component_initialize() {
		const fieldStorage = this.component_find('@:storage:content');
		const fieldBanner = this.component_find('@:storageBanner:content');
		await fieldStorage.component_instantiateOuterChild({
			type :          'input-bool',
			isSlot :        false,
			declaredSlot :  null,
			id :            'options',
			slot :          null,
			tree :          this.component_tree,
			parentNode :    null,
			configuration : <InputBool.Configuration> {
				type :            'yesno',
				defaultValue :    true,
				'@translations' : {
					checked :   {
						'en' : 'Yes',
						'fr' : 'Oui'
					},
					unchecked : {
						'en' : 'No',
						'fr' : 'Non'
					}
				}
			}
		});
		await fieldBanner.component_instantiateOuterChild({
			type :          'input-bool',
			isSlot :        false,
			declaredSlot :  null,
			id :            'options',
			slot :          null,
			tree :          this.component_tree,
			parentNode :    null,
			configuration : <InputBool.Configuration> {
				type :            'yesno',
				defaultValue :    true,
				'@translations' : {
					checked :   {
						'en' : 'Yes',
						'fr' : 'Oui'
					},
					unchecked : {
						'en' : 'No',
						'fr' : 'Non'
					}
				}
			}
		});
		await super.component_initialize();
	};

	override async component_start(startEvent : STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_start(startEvent);
		if (!app.viewer.planner) {
			this.component_throwUserError('');
		}
		const lighting = app.component_configuration.productsComponents.find(x => x.type ===
			'lighting' &&
			x.SKU ===
			'lighting3');
		if (!lighting) {
			throw STD.errors.user('Missing lighting component');
		}
		const storage1x1 = app.component_configuration.productsComponents.filter(x => x.type ===
			'storage' &&
			x.SKU.startsWith('storage1x1')).reduce((a, c) => {
			(<any> a)[ c.SKU.replace('storage1x1', '') ] = c;
			return a;
		}, <Record<'Classic' | 'CornerLeft' | 'CornerRight', typeof app.component_configuration.productsComponents[0]>> {});
		if (!storage1x1.Classic || !storage1x1.CornerLeft || !storage1x1.CornerRight) {
			throw STD.errors.user('Missing a storage 1x1 ');
		}
		const storage2x1 = app.component_configuration.productsComponents.filter(x => x.type ===
			'storage' &&
			x.SKU.startsWith('storage2x1')).reduce(
			(a, c) => {
				(<any> a)[ c.SKU.replace('storage2x1', '') ] = c;
				return a;
			},
			<Record<'Classic' | 'CornerLeft' | 'CornerRight' | 'Side', typeof app.component_configuration.productsComponents[0]>> {}
		);
		if (!storage2x1.Classic || !storage2x1.CornerLeft || !storage2x1.CornerRight || !storage2x1.Side) {
			throw STD.errors.user('Missing a storage 2x1 ');
		}
		const storage3x1 = app.component_configuration.productsComponents.filter(x => x.type ===
			'storage' &&
			x.SKU.startsWith('storage3x1')).reduce(
			(a, c) => {
				(<any> a)[ c.SKU.replace('storage3x1', '') ] = c;
				return a;
			},
			<Record<'Classic' | 'CornerLeft' | 'CornerRight' | 'Side', typeof app.component_configuration.productsComponents[0]>> {}
		);
		if (!storage3x1.Classic || !storage3x1.CornerLeft || !storage3x1.CornerRight || !storage3x1.Side) {
			throw STD.errors.user('Missing a storage 2x1 ');
		}
		if (!storage1x1 || !storage2x1 || !storage3x1) {
			throw STD.errors.user('Missing storage component');
		}
		const storageBannerComponent = app.component_configuration.productsComponents.filter(x => x.type ===
			'storageBanner');
		const defaultExtension : ViewerComponent.IProductExtension = {
			quantity :     null,
			customImages : [
				{
					id :       'customImg',
					title :    { en : 'Add logo', fr : 'Ajouter un logo' },
					get width() {
						const product = app.currentProduct;
						if(!product || !product){
							throw new Error('');
						}
						const productId = product.id;
						if(productId === 'confort'){
							return 0.8;
						}else if(productId === 'siae'){
							return 1;
						}else{
							return 1;
						}
					},
					get height() {
						const product = app.currentProduct;
						if(!product || !product){
							throw new Error('');
						}
						const productId = product.id;
						if(productId === 'confort'){
							return 0.8;
						}else if(productId === 'siae'){
							return 1;
						}else{
							return 1;
						}
					},
					get mesh() {
						const product = app.currentProduct;
						if(!product || !product){
							throw new Error('');
						}
						const productId = product.id;
						if(productId === 'confort'){
							return 'Logo_reserve.001.001';
						}else if(productId === 'siae'){
							return 'Plane.002';
						}else{
							return '';
						}
					},
					get material() {
						const product = app.currentProduct;
						if(!product || !product){
							throw new Error('');
						}
						const productId = product.id;
						if(productId === 'confort'){
							return {
								name : 'custom_logo_reserve_reserve',
								configuration : {
									enabled : 'custom',
									disabled : 'none'
								}
							}
						}else if(productId === 'siae'){
							return {
								name : 'visuel_reserve_siae',
								configuration : {
									enabled : 'visuel_reserve_siae',
									disabled : 'off'
								}
							}
						}else{
							return {
								name : '',
								configuration : {
									enabled : '',
									disabled : ''
								}
							}
						}
					},
					// width :    1,
					// height :   1,
					// mesh :     'Plane.002',
					// material : {
					// 	name :          'visuel_reserve_siae',
					// 	configuration : {
					// 		enabled :  'visuel_reserve_siae',
					// 		disabled : 'off'
					// 	}
					// },
					getLocalId() {
						const product = app.currentProduct;
						if(!product || !product){
							throw new Error('');
						}
						const productId = product.id;
						if(productId === 'confort'){
							return <number> this.idMapping[ 'storageBanner-confort' ];
						}else if(productId === 'siae'){
							return <number> this.idMapping[ 'storageBanner-siae' ];
						}else{
							return 0;
						}
					},
					pricingInfo :       null,
					volumeComputation : null
				}
			],
		};
		const createProduct = (
			viewerComponentSKU : string,
			viewerProjectCode : string,
			localId : string
		) => {
			if(!localId.startsWith('1')){
				app.viewer.productsExtensionById[ `@storage-${ localId }` ] = defaultExtension;
			}
			return {
				SKU :   `@storage-${ localId }`,
				nodes : [
					Object.assign(app.viewer.createEmptyNodeDescriptor('global', 'root', 1), { tracked : true, }),
					{
						SKU :                         viewerComponentSKU,
						projectCode :                 viewerProjectCode,
						localId :                     localId,
						parentLocalId :               'global',
						snappable :                   false,
						fixedPositionSetIds :         null,
						draggable :                   false,
						doNotListAsProductComponent : true,
						tracked :                     true,
						matrix :                      app.viewer.mtx(
							[ 0, 0, 0 ],
							[ 1, 1, 1 ],
							0
						)
					},
					{
						SKU :                         <string> lighting.viewerComponentSKU,
						projectCode :                 <string> lighting.viewerProjectCode,
						localId :                     'lightingStorage',
						parentLocalId :               'global',
						snappable :                   false,
						fixedPositionSetIds :         null,
						draggable :                   false,
						doNotListAsProductComponent : true,
						tracked :                     true,
						matrix :                      app.viewer.mtx(
							[ 0, 0, 0 ],
							[ 0, 0, 0],
							0
						)
					},
					...(storageBannerComponent.map(x => x.products.map(y => ({
						SKU :                         <string> x.viewerComponentSKU,
						projectCode :                 <string> x.viewerProjectCode,
						localId :                     'storageBanner-' + y,
						parentLocalId :               'global',
						snappable :                   false,
						fixedPositionSetIds :         null,
						draggable :                   false,
						doNotListAsProductComponent : true,
						tracked :                     true,
						matrix :                      app.viewer.mtx(
							[ 0, 0, 0 ],
							[ 0, 0, 0 ],
							0
						)
					})))).flat()
				]
			};
		};
		(<any> this).plannerCategory = app.viewer.createCategory({
			name :        'storage',
			menuActions : {
				edit :      true,
				delete :    false,
				rotate :    false,
				duplicate : false
			},
			products :    [
				createProduct(
					<string> storage1x1.Classic.viewerComponentSKU,
					<string> storage1x1.Classic.viewerProjectCode,
					'1x1Classic'
				),
				createProduct(
					<string> storage1x1.CornerLeft.viewerComponentSKU,
					<string> storage1x1.CornerLeft.viewerProjectCode,
					'1x1CornerLeft'
				),
				createProduct(
					<string> storage1x1.CornerRight.viewerComponentSKU,
					<string> storage1x1.CornerRight.viewerProjectCode,
					'1x1CornerRight'
				),
				createProduct(
					<string> storage2x1.Classic.viewerComponentSKU,
					<string> storage2x1.Classic.viewerProjectCode,
					'2x1Classic'
				),
				createProduct(
					<string> storage2x1.CornerLeft.viewerComponentSKU,
					<string> storage2x1.CornerLeft.viewerProjectCode,
					'2x1CornerLeft'
				),
				createProduct(
					<string> storage2x1.CornerRight.viewerComponentSKU,
					<string> storage2x1.CornerRight.viewerProjectCode,
					'2x1CornerRight'
				),
				createProduct(
					<string> storage2x1.Side.viewerComponentSKU,
					<string> storage2x1.Side.viewerProjectCode,
					'2x1Side'
				),
				createProduct(
					<string> storage3x1.Classic.viewerComponentSKU,
					<string> storage3x1.Classic.viewerProjectCode,
					'3x1Classic'
				),
				createProduct(
					<string> storage3x1.CornerLeft.viewerComponentSKU,
					<string> storage3x1.CornerLeft.viewerProjectCode,
					'3x1CornerLeft'
				),
				createProduct(
					<string> storage3x1.CornerRight.viewerComponentSKU,
					<string> storage3x1.CornerRight.viewerProjectCode,
					'3x1CornerRight'
				),
				createProduct(
					<string> storage3x1.Side.viewerComponentSKU,
					<string> storage3x1.Side.viewerProjectCode,
					'3x1Side'
				)
			]
		});
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : IStandingStorageComponentConfiguration
	)
	{
		configuration[ '@innerConfigurations' ] = {
			storage :       <Field.Configuration> {
				'@innerConfigurations' : {
					header : <Block.Configuration> {
						'@innerConfigurations' : {
							label : <Text.Configuration> {
								'@translations' : {
									text : configuration.translations.storage
								}
							}
						}
					}
				}
			},
			storageBanner : <Field.Configuration> {
				'@innerConfigurations' : {
					header : <Block.Configuration> {
						'@innerConfigurations' : {
							label : <Text.Configuration> {
								'@translations' : {
									text : configuration.translations.banner
								}
							}
						}
					}
				}
			}
		};
		super(descriptor, configuration);
		this.component_DOM = <HTMLDivElement> STD.DOM.factory({
			tag : 'div'
		});
	};
};
export namespace NS {
	export const Component = StandingStorageComponent;
	export type Component = StandingStorageComponent;
	export type Configuration = IStandingStorageComponentConfiguration;
}

import manifestXMLSource from './StandingStorage.xml';
import './StandingStorage.scss';

CM.declare({
	id :       'standing-storage',
	type :     StandingStorageComponent,
	manifest : manifestXMLSource
});
